import { useState, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, CheckCircle, XCircle, X, Square } from 'lucide-react'
import { extractionApi, type ExtractedRow } from '@/api/extraction'
import { fieldsApi } from '@/api/fields'
import { useProcessingJob } from '@/hooks/useProcessingJob'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { Badge } from '@/components/ui/badge'
import { ModelProviderDot } from '@/components/ui/model-badge'
import { usePlanLimits } from '@/hooks/usePlanLimits'

function confColor(c: number | null): string {
  if (c === null) return 'text-gray-500 bg-gray-50'
  if (c >= 0.8) return 'text-green-600 bg-green-50'
  if (c >= 0.5) return 'text-yellow-600 bg-yellow-50'
  return 'text-red-600 bg-red-50'
}

interface Props {
  projectId: string
  documentIds: string[]
  onClose: () => void
  onJobStarted?: (jobId: string) => void
}

export function ProcessingModal({ projectId, documentIds, onClose, onJobStarted }: Props) {
  const queryClient = useQueryClient()
  const { isCustomPricing } = usePlanLimits()
  const [selectedModel, setSelectedModel] = useState<string>('')
  const [generateSummary, setGenerateSummary] = useState(true)
  const [jobId, setJobId] = useState<string | null>(null)
  // Per-doc pivoted results: { docId: { docName, fields: { fieldId: { value, confidence } }, avgConf } }
  const [docResults, setDocResults] = useState<Record<string, { docName: string; fields: Record<string, { value: string | null; confidence: number | null }>; avgConf: number | null }>>({})

  const { data: models = [] } = useQuery({
    queryKey: ['plan-models'],
    queryFn: extractionApi.getPlanModels,
    staleTime: 5 * 60 * 1000,
  })

  const { data: fields = [] } = useQuery({
    queryKey: ['fields', projectId],
    queryFn: () => fieldsApi.list(projectId),
  })

  const defaultModel = models.find((m) => m.is_default) || models[0]
  const activeModel = selectedModel || defaultModel?.model_name || ''

  const { data: estimate } = useQuery({
    queryKey: ['credit-estimate', documentIds.join(','), activeModel],
    queryFn: () =>
      extractionApi.estimate({
        document_ids: documentIds,
        selected_model: activeModel,
      }),
    enabled: !!activeModel && documentIds.length > 0,
  })

  const initiateMutation = useMutation({
    mutationFn: () =>
      extractionApi.initiate(projectId, {
        document_ids: documentIds,
        selected_model: activeModel,
        generate_summary: generateSummary,
      }),
    onSuccess: (job) => {
      setJobId(job.id)
      onJobStarted?.(job.id)
    },
  })

  const stopJobMutation = useMutation({
    mutationFn: (id: string) => extractionApi.stopJob(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['extraction-jobs', projectId] })
      queryClient.invalidateQueries({ queryKey: ['documents', projectId] })
      queryClient.invalidateQueries({ queryKey: ['results', projectId] })
    },
  })

  const { events, isComplete, progress } = useProcessingJob(jobId)
  const progressPct = progress ? Math.round((progress.done / progress.total) * 100) : 0

  // Fetch results for each processed document.
  // Note: extractionApi.getResults returns CompactPaginatedResponse
  // ({documents: [{document_id, document_name, rows: [{fields: {[name]: {value, confidence}}}]}], ...})
  // and the compact response keys fields by NAME, not ID — so we have to map
  // names → IDs to populate the table (which renders cells by field id).
  useEffect(() => {
    if (!jobId) return
    const processed = events.filter((e) => e.event === 'document_processed' && e.document_id)
    if (processed.length === 0) return

    const lastEvt = processed[processed.length - 1]
    if (!lastEvt.document_id) return

    // Build a name → id lookup from the project's fields. display_name is used
    // by the compact API, with fallback to name.
    const nameToId = new Map<string, string>()
    for (const f of fields) {
      nameToId.set(f.display_name || f.name, f.id)
    }

    extractionApi
      .getResults(projectId, lastEvt.document_id, jobId)
      .then((res) => {
        const doc = Array.isArray(res?.documents) ? res.documents[0] : null
        if (!doc || !Array.isArray(doc.rows)) return

        const fieldVals: Record<string, { value: string | null; confidence: number | null }> = {}
        let totalConf = 0
        let confCount = 0
        for (const row of doc.rows) {
          if (!row || !row.fields) continue
          for (const [fieldName, fv] of Object.entries(row.fields)) {
            if (!fv) continue
            const fid = nameToId.get(fieldName)
            if (!fid) continue
            fieldVals[fid] = { value: fv.value ?? null, confidence: fv.confidence ?? null }
            if (fv.confidence !== null && fv.confidence !== undefined) {
              totalConf += fv.confidence
              confCount++
            }
          }
        }

        setDocResults((prev) => ({
          ...prev,
          [lastEvt.document_id!]: {
            docName: lastEvt.document_name || lastEvt.document_id!,
            fields: fieldVals,
            avgConf: confCount > 0 ? totalConf / confCount : null,
          },
        }))
      })
      .catch(() => {})
  }, [events.length, jobId, projectId, fields])

  // Invalidate on complete
  useEffect(() => {
    if (isComplete) {
      queryClient.invalidateQueries({ queryKey: ['results', projectId] })
      queryClient.invalidateQueries({ queryKey: ['extraction-jobs', projectId] })
      queryClient.invalidateQueries({ queryKey: ['documents', projectId] })
    }
  }, [isComplete, projectId, queryClient])

  const fieldIds = fields.map((f) => f.id)
  const fieldNames = fields.map((f) => f.display_name || f.name)

  if (jobId) {
    const docRows = Object.entries(docResults)
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-background rounded-lg shadow-xl w-full max-w-3xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Processing Documents</h2>
            <div className="flex items-center gap-1">
              {!isComplete && jobId && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => {
                    if (confirm('Stop this extraction job? Documents already processed will keep their results.')) {
                      stopJobMutation.mutate(jobId)
                    }
                  }}
                  disabled={stopJobMutation.isPending}
                >
                  <Square className="h-3.5 w-3.5 mr-1" />
                  {stopJobMutation.isPending ? 'Stopping...' : 'Stop'}
                </Button>
              )}
              <button onClick={onClose} className="p-1.5 rounded hover:bg-muted">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="space-y-4">
            <Progress value={progressPct} className="h-2" />
            <p className="text-sm text-center text-muted-foreground">
              {progress ? `${progress.done} / ${progress.total} documents` : 'Initializing...'}
            </p>

            {/* Pivoted results table: 1 row per file, fields as columns */}
            {docRows.length > 0 && (
              <div className="max-h-56 overflow-auto border rounded">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50 sticky top-0">
                    <tr>
                      <th className="text-left p-2 font-medium whitespace-nowrap">File</th>
                      {fieldNames.map((name, i) => (
                        <th key={fieldIds[i]} className="text-left p-2 font-medium whitespace-nowrap">{name}</th>
                      ))}
                      <th className="text-center p-2 font-medium whitespace-nowrap">Confidence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {docRows.map(([docId, doc]) => (
                      <tr key={docId} className="border-t">
                        <td className="p-2 font-medium truncate max-w-[160px] whitespace-nowrap" title={doc.docName}>
                          <div className="flex items-center gap-1.5">
                            <CheckCircle className="h-3.5 w-3.5 text-green-500 shrink-0" />
                            {doc.docName}
                          </div>
                        </td>
                        {fieldIds.map((fid) => {
                          const cell = doc.fields[fid]
                          return (
                            <td key={fid} className="p-2 max-w-[150px] truncate whitespace-nowrap" title={cell?.value || ''}>
                              {cell?.value || <em className="text-gray-400">null</em>}
                            </td>
                          )
                        })}
                        <td className="p-2 text-center">
                          {doc.avgConf !== null ? (
                            <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${confColor(doc.avgConf)}`}>
                              {(doc.avgConf * 100).toFixed(0)}%
                            </Badge>
                          ) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Remaining indicator */}
            {!isComplete && progress && progress.done < progress.total && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" />
                Processing {progress.total - progress.done} remaining...
              </div>
            )}

            {isComplete && (
              <div className="flex items-center gap-2 justify-center text-sm text-green-600 font-medium pt-1">
                <CheckCircle className="h-5 w-5" />
                Extraction complete
              </div>
            )}

            {!isComplete && (
              <p className="text-[11px] text-center text-muted-foreground">
                You can close this dialog — processing continues in the background
              </p>
            )}

            {isComplete && (
              <div className="flex gap-2 pt-2">
                <Button onClick={onClose} className="w-full">Done</Button>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Config modal (before job starts)
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg shadow-xl w-full max-w-lg p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Configure Extraction</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-muted">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">Model</label>
          <div className="space-y-2">
            {models.map((model) => (
              <label key={model.model_name}
                className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer ${
                  activeModel === model.model_name ? 'border-primary bg-primary/5' : 'hover:border-muted-foreground/50'
                }`}>
                <div className="flex items-center gap-2">
                  <input type="radio" name="model" value={model.model_name}
                    checked={activeModel === model.model_name} onChange={() => setSelectedModel(model.model_name)} />
                  <ModelProviderDot modelName={model.model_name} />
                  <span className="font-medium text-sm">{model.display_name}</span>
                  {model.is_default && <Badge variant="secondary" className="text-xs">Default</Badge>}
                </div>
                {!isCustomPricing && (
                  <div className="text-xs text-muted-foreground text-right">
                    {Number(model.credits_per_page_batch_mode).toFixed(1)} cr/page
                  </div>
                )}
              </label>
            ))}
          </div>
        </div>

        {/* Summary toggle */}
        <div className="flex items-center gap-2 px-1">
          <input
            type="checkbox"
            id="generate-summary"
            checked={generateSummary}
            onChange={(e) => setGenerateSummary(e.target.checked)}
          />
          <label htmlFor="generate-summary" className="text-sm cursor-pointer">
            Generate AI summary per document
          </label>
          {!isCustomPricing && <span className="text-xs text-muted-foreground ml-auto">+1 chat credit/doc</span>}
        </div>

        <div className="bg-muted/50 rounded-lg p-3 text-sm space-y-1">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Documents</span>
            <span>{documentIds.length}</span>
          </div>
          {estimate && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total pages</span>
              <span>{estimate.total_pages}</span>
            </div>
          )}
          {!isCustomPricing && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                Rate
              </span>
              <span>
                {estimate ? `${estimate.rate_per_page.toFixed(1)} cr / page` : '...'}
              </span>
            </div>
          )}
          {!isCustomPricing && (
            <div className="flex justify-between pt-1 border-t mt-1">
              <span className="text-muted-foreground">Estimated cost</span>
              <span className="font-medium">
                {estimate ? `${Math.ceil(estimate.estimated_credits)} credits` : '...'}
              </span>
            </div>
          )}
          {estimate && estimate.per_document.length > 1 && (
            <details className="pt-2 mt-1 border-t">
              <summary className="cursor-pointer text-xs text-muted-foreground">
                Per-document breakdown
              </summary>
              <ul className="mt-2 space-y-1 text-xs max-h-40 overflow-y-auto">
                {estimate.per_document.map((d) => (
                  <li key={d.document_id} className="flex justify-between gap-2">
                    <span className="truncate">{d.document_name ?? d.document_id}</span>
                    <span className="text-muted-foreground shrink-0">
                      {d.page_count} pg · {Math.ceil(d.credits)} cr
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>

        <div className="flex gap-2">
          <Button className="flex-1" onClick={() => initiateMutation.mutate()} disabled={!activeModel || initiateMutation.isPending}>
            {initiateMutation.isPending ? <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Starting...</> : 'Start Extraction'}
          </Button>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
        </div>
      </div>
    </div>
  )
}
